import React, {lazy, Suspense} from 'react';
import { Navigate, Route, Routes, useLocation, Outlet } from 'react-router-dom';
import { Box } from '@mui/material';
import AdminLayout from '../pages/admin/common/AdminLayout';
import MainContainer from '../components/common/MainContainer';
import PrivateRoutes from './PrivateRoutes';
import PublicRoutes from './PublicRoutes';

const MobileAppLayout = () => {
  return (
    <Box maxWidth="470px" width="100%" margin="0 auto" position="relative" sx={{ backgroundColor: 'inherit' }}>
      <Outlet />
    </Box>
  );
};

import Loader from '../components/common/Loader';

// Public routes - load immediately (not lazy)
import Login from '../components/modules/Auth/Login';
import Register from '../components/modules/Auth/Register';

// All other routes - lazy load
const Home = lazy(() => import('../pages/Home'));
const Journey = lazy(() => import('../pages/Journey'));
const JourneyRouter = lazy(() => import('../pages/JourneyRouter'));
const CreateGroup = lazy(() => import('../pages/CreateGroup'));
const LeaderHome = lazy(() => import('../pages/LeaderHome'));
const SuperAdmin = lazy(() => import('../pages/SuperAdmin'));
const Assessment = lazy(() => import('../pages/Assesment'));
const DiscipleOverview = lazy(() => import('../pages/DiscipleOverview'));
const HowTheyAreDoingHelpPage = lazy(() => import('../pages/HowTheyAreDoingHelpPage.jsx'));
const TitleHelpModa = lazy(() => import('../pages/TitleHelpModa.jsx'));
const GroupEmail = lazy(() => import('../pages/GroupEmail'));
const EditGroup = lazy(() => import('../pages/EditGroup'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const DiscipleHome = lazy(() => import('../pages/DiscipleHome'));
const EditProfile = lazy(() => import('../pages/EditProfile'));
const ProfileStep1 = lazy(() => import('../pages/ProfileStep1'));
const ProfileStep2 = lazy(() => import('../pages/ProfileStep2'));
const ProfileStep3 = lazy(() => import('../pages/ProfileStep3'));
const ContactUs = lazy(() => import('../pages/ContactUs'));
const StartMeeting = lazy(() => import('../pages/StartMeeting.jsx'));
const ReadingPlan = lazy(() => import('../pages/ReadingPlan.jsx'));
const EmailLeader = lazy(() => import('../pages/EmailLeader.jsx'));
const GetStarted = lazy(() => import('../pages/GetStarted.jsx'));
const Groups = lazy(() => import('../pages/admin/Groups.jsx'));
const Leaders = lazy(() => import('../pages/admin/Leaders.jsx'));
const Disciples = lazy(() => import('../pages/admin/Disciples.jsx'));
const Admins = lazy(() => import('../pages/admin/Admins.jsx'));
const Settings = lazy(() => import('../pages/admin/Settings.jsx'));
const Promotions = lazy(() => import('../pages/admin/Promotions.jsx'));
const Reports = lazy(() => import('../pages/admin/Reports.jsx'));
const Dashboard = lazy(() => import('../pages/admin/Dashboard.jsx'));
const Attendance = lazy(() => import('../pages/Attendance.jsx'));
const MarkAttendance = lazy(() => import('../pages/MarkAttendance.jsx'));
const ChangePassword = lazy(() => import('../pages/ChangePassword.jsx'));
const AdminChangePassword = lazy(() => import('../pages/admin/AdminChangePassword.jsx'));
const ReadingPlans = lazy(() => import('../pages/admin/ReadingPlans.jsx'));
const Organisations = lazy(() => import('../pages/superadmin/Organisations.jsx'));
const Campaign = lazy(() => import('../pages/superadmin/Campaign.jsx'));
const CampaignReport = lazy(() => import('../pages/superadmin/CampaignReport.jsx'));
const UnassignedUsers = lazy(() => import('../pages/superadmin/UnassignedUsers.jsx'));
const AdminProfile = lazy(() => import('../pages/admin/components/AdminProfile.jsx'));
const DraftDay = lazy(() => import('../pages/admin/DraftDay.jsx'));
const AllUsers = lazy(() => import('../pages/admin/AllUsers.jsx'));
const PreviewDisciple = lazy(() => import('../pages/PreviewDisciple.jsx'));
const NotesBlinksScreen = lazy(() => import('../pages/NotesBlinksScreen.jsx'));
const MobileRouteWrapper = lazy(() => import('../components/MobileRouteWrapper.js'));

// Component to handle journey redirect with query parameters
const JourneyRedirect = () => {
  const location = useLocation();
  const queryParams = location.search; // Gets the query string including ?
  return <Navigate to={`/journey/1${queryParams}`} replace />;
};

const ProfileRedirect = () => {
  return <Navigate to="/profile-step-1?isEdit=true" replace />;
};

const publicRoutes = [
  {
    id: 1,
    path: '/login',
    element: <Login />
  },
  {
    id: 2,
    path: '/signup',
    element: <Register />
  },
  {
    id: 3,
    path: '/forgot-password',
    element: <ForgotPassword />
  },
  {
    id: 4,
    path: '/get-started',
    element: <GetStarted />
  },
  {
    id: 5,
    path: '*',
    element: <Navigate to="/get-started" />
  }
];

const mobilePublicRoutes = [
  {
    id: 1,
    path: '/mobile/login',
    element: (
      <MobileRouteWrapper>
        <Login />
      </MobileRouteWrapper>
    )
  },
  {
    id: 2,
    path: '/mobile/signup',
    element: (
      <MobileRouteWrapper>
        <Register />
      </MobileRouteWrapper>
    )
  },
  {
    id: 3,
    path: '/mobile/forgot-password',
    element: (
      <MobileRouteWrapper>
        <ForgotPassword />
      </MobileRouteWrapper>
    )
  },
  {
    id: 4,
    path: '/mobile/get-started',
    element: (
      <MobileRouteWrapper>
        <GetStarted />
      </MobileRouteWrapper>
    )
  },
  {
    id: 5,
    path: '/mobile/*',
    element: <Navigate to="/mobile/get-started" />
  }
];
const superadminRoutes = [
  // {
  //   id: 1,
  //   path: '/super-admin',
  //   element: <SuperAdmin />
  // },
  {
    id: 2,
    path: '/super-admin/dashboard',
    element: <Dashboard isSuperAdmin={true} />
  },
  {
    id: 3,
    path: '/super-admin/organization',
    element: <Organisations />
  },
  {
    id: 4,
    path: '/super-admin/reading-plans',
    element: <ReadingPlans role="SUPERADMIN" />
  },
  {
    id: 5,
    path: '/super-admin/reports',
    element: <Reports />
  },
  {
    id: 7,
    path: '*',
    element: <Navigate to="/super-admin" />
  },
  {
    id: 8,
    path: '/super-admin/all-users',
    element: <AllUsers role="SUPERADMIN" />
  },
  {
    id: 13,
    path: '/super-admin/unassigned-users',
    element: <UnassignedUsers />
  },
  {
    id: 9,
    path: '/super-admin/change-password',
    element: <AdminChangePassword />
  },
  {
    id: 10,
    path: '/super-admin/profile',
    element: <AdminProfile />
  },
  {
    id: 11,
    path: '/super-admin/campaign',
    element: <Campaign />
  },
  {
    id: 12,
    path: '/super-admin/campaign-report',
    element: <CampaignReport />
  }
];
const adminRoutes = [
  {
    id: 1,
    path: '/admin-dashboard/groups',
    element: <Groups />
  },
  {
    id: 2,
    path: '/admin-dashboard/leaders',
    element: <Leaders />
  },
  {
    id: 3,
    path: '/admin-dashboard/disciples',
    element: <Disciples />
  },
  {
    id: 4,
    path: '/admin-dashboard/admins',
    element: <Admins />
  },
  {
    id: 5,
    path: '/admin-dashboard/reading-plans',
    element: <ReadingPlans role="ADMIN" />
  },
  {
    id: 6,
    path: '/admin-dashboard/settings',
    element: <Settings />
  },
  {
    id: 7,
    path: '/admin-dashboard/promotions',
    element: <Promotions />
  },
  {
    id: 8,
    path: '/admin-dashboard/reports',
    element: <Reports />
  },
  {
    id: 9,
    path: '/admin-dashboard',
    element: <Dashboard isAdmin={true} />
  },
  {
    id: 10,
    path: '*',
    element: <Navigate to="/admin-dashboard" />
  },
  {
    id: 11,
    path: '/admin-dashboard/change-password',
    element: <AdminChangePassword />
  },
  {
    id: 12,
    path: '/admin-dashboard/profile',
    element: <AdminProfile />
  },
  {
    id: 13,
    path: '/admin-dashboard/draftday',
    element: <DraftDay />
  },
  {
    id: 14,
    path: '/admin-dashboard/all-users',
    element: <AllUsers role="ADMIN" />
  }
];
const leaderRoutes = [
  {
    id: 1,
    path: '/leader',
    element: <LeaderHome />
  },
  {
    id: 2,
    path: '/leader/meetings',
    element: <StartMeeting />
  },
  {
    id: 3,
    path: '/leader/reading-plans',
    element: <ReadingPlan />
  },
  {
    id: 4,
    path: '/leader/outside-reading-plans',
    element: <ReadingPlan />
  },
  {
    id: 5,
    path: '/group/:name/:id',
    element: <Home />
  },
  {
    id: 6,
    path: '/group/send-email/:name/:id',
    element: <GroupEmail />
  },
  {
    id: 7,
    path: '/edit-group/:id',
    element: <EditGroup />
  },
  {
    id: 8,
    path: '/assessment/:level/:groupId/:userId',
    element: <Assessment />
  },
  {
    id: 13,
    path: '/disciple-overview/:level/:groupId/:userId',
    element: <DiscipleOverview />
  },
  {
    id: 14,
    path: "/help/how-they-are-doing",
    element: <HowTheyAreDoingHelpPage />
  },
  {
    id: 15,
    path: "/help/who-they-are",
    element: <TitleHelpModa />
  },
  {
    id: 16,
    path: "/help/notes-and-blink",
    element: <NotesBlinksScreen />
  },
  {
    id: 9,
    path: '/create-group',
    element: <CreateGroup />
  },
  {
    id: 10,
    path: '*',
    element: <Navigate to="/leader" />
  },
  {
    id: 11,
    path: '/group/attendance/:name/:id/:meetingId',
    element: <Attendance />
  },
  {
    id: 12,
    path: '/group/preview-disciple/:name/:id',
    element: <PreviewDisciple />
  },
];
const discipleRoutes = [
  {
    id: 1,
    path: '/disciple',
    element: <DiscipleHome />
  },
  {
    id: 2,
    path: '/disciple/email-leader',
    element: <EmailLeader />
  },
  {
    id: 3,
    path: '*',
    element: <Navigate to="/disciple" />
  },
  {
    id: 4,
    path: '/disciple/mark-attendance',
    element: <MarkAttendance />
  }
];
const discipleLeaderRoutes = [
  {
    id: 1,
    path: '/journey',
    element: <JourneyRedirect />
  },
  {
    id: 2,
    path: '/journey/:step',
    element: <JourneyRouter />
  },
  {
    id: 3,
    path: '*',
    element: <Navigate to="/disciple" />
  }
];
const discipleLeaderAdminRoutes = [
  {
    id: 1,
    path: '/edit-profile',
    element: <ProfileRedirect />
  },
  {
    id: 2,
    path: '/profile-step-1',
    element: <ProfileStep1 />
  },
  {
    id: 3,
    path: '/profile-step-2',
    element: <ProfileStep2 />
  },
  {
    id: 4,
    path: '/profile-step-3',
    element: <ProfileStep3 />
  },
  {
    id: 5,
    path: '/contact-us',
    element: <ContactUs />
  },
  {
    id: 6,
    path: '*',
    element: <Navigate to="/disciple" />
  },
  {
    id: 7,
    path: '/change-password',
    element: <ChangePassword />
  }
];

const ProjectRoutes = () => {
  return (
    <MainContainer>
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Auth routes */}
          <Route element={<PublicRoutes />}>
            {publicRoutes.map(route => (
              <Route key={route.id} path={route.path} element={route.element} />
            ))}
          </Route>
          <Route element={<PublicRoutes />}>
            {mobilePublicRoutes.map(route => (
              <Route key={`mobile-${route.id}`} path={route.path} element={route.element} />
            ))}
          </Route>
          {/* SUPERADMIN ROUTES — AdminLayout provides the persistent shell */}
          <Route element={<PrivateRoutes roles={['SUPERADMIN']} />}>
            <Route element={<AdminLayout />}>
              {superadminRoutes.map(route => (
                <Route key={route.id} path={route.path} element={route.element} />
              ))}
            </Route>
          </Route>
          {/* ADMIN ROUTES — AdminLayout provides the persistent shell */}
          <Route element={<PrivateRoutes roles={['ADMIN']} />}>
            <Route element={<AdminLayout />}>
              {adminRoutes.map(route => (
                <Route key={route.id} path={route.path} element={route.element} />
              ))}
            </Route>
          </Route>
          <Route element={<MobileAppLayout />}>
            {/* LEADER ROUTES */}
            <Route element={<PrivateRoutes roles={['LEADER']} />}>
              {leaderRoutes.map(route => (
                <Route key={route.id} path={route.path} element={route.element} />
              ))}
            </Route>
            {/* DISCIPLE ROUTES */}
            <Route element={<PrivateRoutes roles={['DISCIPLE']} />}>
              {discipleRoutes.map(route => (
                <Route key={route.id} path={route.path} element={route.element} />
              ))}
            </Route>
            {/* MERGED DISCIPLE AND LEADER ROUTES */}
            <Route element={<PrivateRoutes roles={['DISCIPLE', 'LEADER']} />}>
              {discipleLeaderRoutes.map(route => (
                <Route key={route.id} path={route.path} element={route.element} />
              ))}
            </Route>
            {/* MERGED DISCIPLE, ADMIN and LEADER routes */}
            <Route element={<PrivateRoutes roles={['DISCIPLE', 'ADMIN', 'LEADER']} />}>
              {discipleLeaderAdminRoutes.map(route => (
                <Route key={route.id} path={route.path} element={route.element} />
              ))}
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </MainContainer>
  );
};

export default ProjectRoutes;